/**
 * Orari di lavoro e blocchi ricorrenti per persona (regole pure, test in __tests__/workHours.test.ts).
 *
 * Vivono in `settings.calendar_settings.availability`: un orario di base dell'azienda e, per chi serve,
 * il suo orario e i suoi blocchi (pausa pranzo, riunione fissa, giornata chiusa). La vista Team e
 * l'avviso nel modulo li trattano come "occupato": l'operatrice non fissa sopra la pausa del consulente.
 * Minuti dalla mezzanotte, giorni come Date.getDay() (0 = domenica).
 */
import { DEFAULT_WORK_HOURS, type Interval } from './availability';

export type WeekHours = (Interval | null)[];
export interface AvailabilityBlock {
  id: string;
  title: string;
  days: number[];
  start: number;
  end: number;
  /** AAAA-MM-GG, compresi; vuoti = sempre */
  from?: string | null;
  until?: string | null;
}
export interface PersonAvailability { hours?: WeekHours | null; blocks?: AvailabilityBlock[] }
export interface OrgAvailability { default?: WeekHours | null; people?: Record<string, PersonAvailability> }
export interface DayBlock extends Interval { id: string; title: string }

/** Lunedi-venerdi 9-18: lo stesso orario usato finora. */
export const BASE_WEEK: WeekHours = [0, 1, 2, 3, 4, 5, 6].map(d => (d === 0 || d === 6 ? null : { ...DEFAULT_WORK_HOURS }));
export const WEEKDAY_LABELS = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
export const WEEKDAY_SHORT = ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'];
/** Ordine di lettura italiano: da lunedi a domenica. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function dayKey(day: Date): string {
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
}

function validWeek(w: WeekHours | null | undefined): w is WeekHours {
  return Array.isArray(w) && w.length === 7;
}

/** La settimana di una persona: la sua, altrimenti quella dell'azienda, altrimenti lunedi-venerdi 9-18. */
export function weekFor(org: OrgAvailability | null | undefined, personId: string | null | undefined): WeekHours {
  const own = personId ? org?.people?.[personId]?.hours : null;
  if (validWeek(own)) return own;
  if (validWeek(org?.default)) return org!.default!;
  return BASE_WEEK;
}

/** Orario di lavoro della persona in quel giorno; null se quel giorno non lavora. */
export function workWindowFor(day: Date, org: OrgAvailability | null | undefined, personId?: string | null): Interval | null {
  const h = weekFor(org, personId)[day.getDay()];
  return h && h.end > h.start ? { start: h.start, end: h.end } : null;
}

/** I blocchi della persona che cadono in quel giorno, in ordine di ora. */
export function blocksFor(day: Date, org: OrgAvailability | null | undefined, personId?: string | null): DayBlock[] {
  const list = (personId && org?.people?.[personId]?.blocks) || [];
  const key = dayKey(day);
  const wd = day.getDay();
  return list
    .filter(b => b.end > b.start && b.days.includes(wd) && (!b.from || key >= b.from) && (!b.until || key <= b.until))
    .map(b => ({ id: b.id, title: b.title || 'Non disponibile', start: b.start, end: b.end }))
    .sort((a, b) => a.start - b.start);
}

/** Le ore da mostrare nella griglia: almeno 8-19, allargate per chi lavora prima o dopo. */
export function gridRange(windows: readonly (Interval | null)[], min: Interval = { start: 8 * 60, end: 19 * 60 }): Interval {
  let start = min.start;
  let end = min.end;
  for (const w of windows) {
    if (!w) continue;
    start = Math.min(start, Math.floor(w.start / 60) * 60);
    end = Math.max(end, Math.ceil(w.end / 60) * 60);
  }
  return { start, end };
}

/** "lu-ve 9:00-18:00" per riassumere una settimana in una riga. */
export function describeWeek(week: WeekHours, fmt: (m: number) => string): string {
  const parts: string[] = [];
  let i = 0;
  while (i < WEEK_ORDER.length) {
    const h = week[WEEK_ORDER[i]];
    let j = i;
    while (j + 1 < WEEK_ORDER.length && same(week[WEEK_ORDER[j + 1]], h)) j++;
    if (h) {
      const days = i === j ? WEEKDAY_SHORT[WEEK_ORDER[i]].toLowerCase() : `${WEEKDAY_SHORT[WEEK_ORDER[i]].toLowerCase()}-${WEEKDAY_SHORT[WEEK_ORDER[j]].toLowerCase()}`;
      parts.push(`${days} ${fmt(h.start)}-${fmt(h.end)}`);
    }
    i = j + 1;
  }
  return parts.length ? parts.join(', ') : 'nessun giorno di lavoro';
}

function same(a: Interval | null, b: Interval | null): boolean {
  return a === b || (!!a && !!b && a.start === b.start && a.end === b.end);
}

/** Toglie blocchi vuoti o senza giorni e persone senza nulla da salvare. */
export function cleanAvailability(a: OrgAvailability): OrgAvailability {
  const people: Record<string, PersonAvailability> = {};
  for (const [id, p] of Object.entries(a.people || {})) {
    const blocks = (p.blocks || []).filter(b => b.end > b.start && b.days.length).map(b => ({ ...b, title: b.title.trim() || 'Non disponibile' }));
    const hours = validWeek(p.hours) ? p.hours : null;
    if (hours || blocks.length) people[id] = { ...(hours ? { hours } : {}), ...(blocks.length ? { blocks } : {}) };
  }
  return { ...(validWeek(a.default) ? { default: a.default } : {}), people };
}
